// src/pages/FormReaderTable.jsx
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import Table from "../shared/table/Table";
import { FiUpload, FiTrash2, FiPlus } from "react-icons/fi";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { confirmAlert } from "react-confirm-alert";
import UploadToFormReaderModal from "../UploadToFormReaderModal";
import useUserLanguage from "../../hooks/useUserLanguage";
import useTranslations from "../../hooks/useTranslations";


const API = `${process.env.REACT_APP_API_BASE_URL}/api`;
const authHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` }
});

export default function FormReaderTable() {
  const [rows, setRows] = useState([]);
  const [uploadModal, setUploadModal] = useState({ open: false, readerId: null });

  const lang = useUserLanguage();
  const { translations } = useTranslations(lang);
  const t = (k, d = "") => translations[k] || d;

  const fetchData = () => {
    axios
      .get(`${API}/form-reader`, authHeaders())
      .then(r => setRows(r.data))
      .catch(console.error);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const columns = useMemo(() => [
    { accessorKey: "id", header: "ID", size: 60 },

    {
      accessorKey: "opticalForm.title",
      header: t("FORM_NAME","Form Adı"),
      cell: info => info.getValue() || " "
    },
    {
      accessorKey: "institution.title",
      header: t("INSTITUTION","Kurum"),
      cell: info => info.getValue() || " "
    },
    {
      accessorKey: "readerClass.title",
      header: t("CLASS","Sınıf"),
      cell: info => info.getValue() || " "
    },
    {
      accessorKey: "educationYear.title",
      header: t("EDU_YEAR","Eğitim Yılı"),
      cell: info => info.getValue() || " "
    },
    {
      accessorKey: "created_at",
      header: t("DATE","Tarih"),
      size: 120,
      cell: info =>
        new Date(info.getValue()).toLocaleDateString("tr-TR", {
          day: "2-digit", month: "2-digit", year: "numeric"
        })
    },

    {
      accessorKey: "actions",
      header: t("ACTIONS","İşlemler"),
      enableSorting: false,
      cell: info => {
        const { id, processed } = info.row.original;

            const startProcess = () => {
              confirmAlert({
                title: t("START_SCAN_CONFIRM_TITLE", "Taramayı Başlat"),
                message: t("START_SCAN_CONFIRM",       "Taramayı başlatmak istiyor musunuz?"),
                buttons: [
                  {
                    label: t("YES", "Evet"),
                    onClick: async () => {
                      // 1) Loading toast aç
                      const toastId = toast.loading(
                        t("SCAN_IN_PROGRESS", "Tarama başlatıldı."),
                        { position: "top-right", autoClose: false, closeOnClick: false }
                      );

                      try {
                        await axios.post(
                          `${API}/processing/${id}/start`,
                          {},
                          authHeaders()
                        );

                        // 2) Başarı durumuna güncelle
                        toast.update(toastId, {
                          render:    t("SCAN_DONE",       "Tarama tamamlandı"),
                          type:      "success",
                          isLoading: false,
                          autoClose: 3000,
                          closeButton: true,
                          closeOnClick: true,
                        });
                        fetchData();
                      } catch (err) {
                        console.error(err);
                        // 3) Hata durumuna güncelle
                        toast.update(toastId, {
                          render:      t("SCAN_ERROR",       "Tarama sırasında hata oluştu"),
                          type:        "error",
                          isLoading:   false,
                          autoClose:   5000,
                          closeButton: true,
                          closeOnClick: true,
                        });
                      }
                    }
                  },
                  {
                    label: t("NO", "Hayır"),
                    // hiçbir şey yapma, dialog kapanacak
                  }
                ]
              });
            };

          const handleDelete = async () => {
            confirmAlert({
              title: t("CONFIRM_DELETE", "Silme Onayı"),
              message: t("CONFIRM_DELETE_MSG", "Bu öğeyi silmek istediğinize emin misiniz?"),
              buttons: [
                {
                  label: t("YES", "Evet, sil"),
                  onClick: async () => {
                    try {
                      await axios.delete(`${API}/form-reader/${id}`, authHeaders());
                      toast.success(t("DELETED", "Başarıyla silindi"));
                      fetchData(); // tabloyu yenile
                    } catch (err) {
                      console.error(err);
                      toast.error(t("DELETE_ERROR", "Silme sırasında hata oluştu"));
                    }
                  }
                },
                {
                  label: t("CANCEL", "İptal"),
                  onClick: () => {} // İptal edildiğinde bir şey yapma
                }
              ]
            });
          };
        return (
          <div className="hstack gap-2 justify-content-end">
            {/* Taramayı Başlat */}
            <button
              type="button"
              onClick={startProcess}
              disabled={processed}
              className={
                "avatar-text avatar-md " +
                (processed
                  ? "text-secondary bg-soft-secondary border-soft-secondary opacity-50"
                  : "text-primary bg-soft-primary border-soft-primary")
              }
              title={
                processed
                  ? t("ALREADY_SCANNED","Daha önce tarandı.")
                  : t("START_SCAN","Taramayı Başlat")
              }
            >
              <FiUpload />
            </button>

            {/* Sayfa Ekle */}
            <button
              type="button"
              className="avatar-text avatar-md text-success bg-soft-success border-soft-success"
              title={t("ADD_PAGE","Sayfa Ekle")}
              onClick={() => setUploadModal({ open: true, readerId: id })}
            >
              <FiPlus />
            </button>

            {/* Sil */}
            <button
              type="button"
              onClick={handleDelete}
              className="avatar-text avatar-md text-danger bg-soft-danger border-soft-danger"
              title={t("DELETE","Sil")}
            >
              <FiTrash2 />
            </button>
          </div>
        );
      }
    }
  ], [translations]);

  return (
    <>
      <Table data={rows} columns={columns} />

      {uploadModal.open && (
        <UploadToFormReaderModal
          formReaderId={uploadModal.readerId}
          close={() => setUploadModal({ open: false, readerId: null })}
          refresh={fetchData}
        />
      )}
    </>
  );
}
